import { Clock } from 'lucide-react'
import type { StationWithDistance } from '@fuelfinder/shared'

interface CompactStationCardProps {
  station: StationWithDistance
  fuelType: 'E10' | 'B7'
  pricePercentile: number
  onClick: () => void
}

/**
 * Get Tailwind text color class based on price percentile
 */
function getPriceColorClass(percentile: number): string {
  if (percentile <= 25) return 'text-green-600'
  if (percentile <= 75) return 'text-amber-500'
  return 'text-red-500'
}

/**
 * Get Tailwind border color class based on price percentile
 */
function getPriceBorderClass(percentile: number): string {
  if (percentile <= 25) return 'border-l-green-500'
  if (percentile <= 75) return 'border-l-amber-400'
  return 'border-l-red-400'
}

/**
 * Format price in pence to display format (e.g., 131.9p)
 */
function formatPrice(pricePence: number | null): string {
  if (pricePence === null) return 'N/A'
  return `${pricePence.toFixed(1)}p`
}

/**
 * Format distance in miles (e.g., "0.3 mi away")
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi away'
  return `${miles.toFixed(1)} mi away`
}

/**
 * Format date string to compact relative time with "ago" suffix
 */
function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
  } catch {
    return ''
  }
}

/**
 * Compact station card for use in the draggable sheet list.
 * Shows brand, price, distance only. Single tap opens detail sheet.
 */
export function CompactStationCard({
  station,
  fuelType,
  pricePercentile,
  onClick,
}: CompactStationCardProps) {
  const price = station.prices[fuelType]
  const priceColorClass = getPriceColorClass(pricePercentile)
  const borderClass = getPriceBorderClass(pricePercentile)

  return (
    <button
      onClick={onClick}
      className={`compact-station-card w-full text-left bg-white dark:bg-slate-800 border-l-4 ${borderClass} px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors`}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: Brand and distance */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
            {station.brand}
          </h3>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-0.5">
            {formatDistance(station.distanceMiles)}
          </p>
        </div>

        {/* Right: Price and updated time */}
        <div className="flex flex-col items-end">
          <span className={`text-2xl font-bold ${priceColorClass} tabular-nums`}>
            {formatPrice(price)}
          </span>
          <span className="flex items-center gap-0.5 text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            <Clock size={10} />
            Updated {formatRelativeTime(station.updatedAt)}
          </span>
        </div>
      </div>
    </button>
  )
}
